import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { facilitiesAPI, connectAPI } from '@/lib/api';
import { openCheckout } from '@/lib/stripe';
import { canSellPremium } from '@/lib/appUrl';
import { FacilityFieldsManager } from '@/components/FacilityFields';
import { FacilityBookingDialog, formatDateStr } from '@/components/FacilityBookingDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, CalendarDays, Euro, TrendingUp, Clock,
  CheckCircle, AlertCircle, CreditCard, Landmark,
  Calendar, BarChart3, Settings, ChevronLeft, ChevronRight,
  MapPin, Eye, Plus,
} from 'lucide-react';

// Day names for charts
const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

// ── Main Component ─────────────────────────────────────────────────────────────

export const FacilityDashboardPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');

  // Read tab from URL hash on mount and on hash change
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (['bookings', 'fields', 'payments', 'stats'].includes(hash)) {
      setActiveTab(hash);
    } else if (hash === 'overview') {
      setActiveTab('stats');
    }
  }, [location.hash]);

  // Bookings list tab
  const [allBookings, setAllBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingFilter, setBookingFilter] = useState('upcoming');
  const [fieldFilter, setFieldFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  });

  // Bank details
  const [bankDetails, setBankDetails] = useState(null);
  const [bankForm, setBankForm] = useState({ iban: '', holder_name: '', swift: '' });
  const [savingBank, setSavingBank] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState(0);

  // Mark paid / cancel dialogs, new booking
  const [markPaidBooking, setMarkPaidBooking] = useState(null);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    try {
      const res = await facilitiesAPI.getDashboardStats(id);
      setStats(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Non sei autorizzato a visualizzare questa dashboard');
        navigate('/facilities');
      } else {
        toast.error('Errore nel caricamento dei dati');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchBookings = useCallback(async () => {
    setLoadingBookings(true);
    try {
      const today = formatDateStr(new Date());
      let params = {};
      if (bookingFilter === 'upcoming') {
        params.date_from = today;
      } else if (bookingFilter === 'past') {
        params.date_to = today;
      } else if (bookingFilter === 'week') {
        const from = formatDateStr(calendarWeekStart);
        const toDate = new Date(calendarWeekStart);
        toDate.setDate(toDate.getDate() + 6);
        params.date_from = from;
        params.date_to = formatDateStr(toDate);
      }
      if (fieldFilter !== 'all') {
        params.field_id = fieldFilter;
      }
      const res = await facilitiesAPI.getBookings(id, params);
      let bookings = res.data || [];
      if (paymentFilter !== 'all') {
        bookings = bookings.filter(b => {
          if (paymentFilter === 'paid') return b.payment_status === 'paid';
          if (paymentFilter === 'unpaid') return b.payment_status !== 'paid' && b.payment_status !== 'not_required';
          if (paymentFilter === 'at_venue') return b.payment_method === 'at_venue';
          if (paymentFilter === 'online') return b.payment_method === 'online';
          return true;
        });
      }
      setAllBookings(bookings);
    } catch {
      toast.error('Errore nel caricamento prenotazioni');
    } finally {
      setLoadingBookings(false);
    }
  }, [id, bookingFilter, fieldFilter, paymentFilter, calendarWeekStart]);

  const fetchBankInfo = useCallback(async () => {
    try {
      const [bankRes, balRes] = await Promise.all([
        facilitiesAPI.getBankDetails(id),
        facilitiesAPI.getBalance(id),
      ]);
      setBankDetails(bankRes.data);
      setBalanceData(balRes.data);
      setPayoutAmount(balRes.data?.balance_owed || 0);
      if (bankRes.data?.has_bank_details) {
        setBankForm({
          iban: bankRes.data.iban || '',
          holder_name: bankRes.data.holder_name || '',
          swift: bankRes.data.swift || '',
        });
      }
    } catch {
      // ignore
    }
  }, [id]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeTab === 'bookings') fetchBookings(); }, [activeTab, fetchBookings]);
  useEffect(() => { if (activeTab === 'payments') fetchBankInfo(); }, [activeTab, fetchBankInfo]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleMarkPaid = async (bookingId) => {
    try {
      await facilitiesAPI.markBookingPaid(bookingId);
      toast.success('Pagamento registrato');
      setMarkPaidBooking(null);
      fetchBookings();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await facilitiesAPI.cancelBooking(bookingId);
      toast.success('Prenotazione annullata');
      setCancelBooking(null);
      fetchBookings();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleSaveBank = async () => {
    setSavingBank(true);
    try {
      await facilitiesAPI.saveBankDetails(id, bankForm);
      toast.success('Dati bancari salvati');
      fetchBankInfo();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingBank(false);
    }
  };

  const navigateWeek = (dir) => {
    setCalendarWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + dir * 7);
      return d;
    });
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const formatDateDisplay = (dateStr) => {
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('it-IT', {
        weekday: 'short', day: 'numeric', month: 'short',
      });
    } catch { return dateStr; }
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="max-w-7xl mx-auto p-4 text-center py-20">
        <p className="text-muted-foreground">Dashboard non disponibile</p>
        <Button variant="link" onClick={() => navigate('/facilities')}>Torna alle strutture</Button>
      </div>
    );
  }

  const { kpi, facility } = stats;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Gestione struttura</p>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6 shrink-0" />
            <span className="truncate">{facility.name}</span>
          </h1>
          {facility.city && <p className="text-sm text-muted-foreground">{facility.city}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/facilities/${id}`}>
              <Eye className="h-4 w-4 mr-1" /> Vedi come i giocatori
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/facilities/${id}/edit`}>
              <Settings className="h-4 w-4 mr-1" /> Modifica dati
            </Link>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full md:w-auto grid grid-cols-4 md:flex">
          <TabsTrigger value="bookings"><Calendar className="h-4 w-4 mr-1 hidden sm:inline" /> Prenotazioni</TabsTrigger>
          <TabsTrigger value="fields"><MapPin className="h-4 w-4 mr-1 hidden sm:inline" /> Campi</TabsTrigger>
          <TabsTrigger value="payments"><Euro className="h-4 w-4 mr-1 hidden sm:inline" /> Pagamenti</TabsTrigger>
          <TabsTrigger value="stats"><BarChart3 className="h-4 w-4 mr-1 hidden sm:inline" /> Statistiche</TabsTrigger>
        </TabsList>

        {/* ═══ TAB: STATISTICHE ═══ */}
        <TabsContent value="stats" className="space-y-6">
          {/* Premium banner / active badge */}
          {facility.tier === 'premium'
            ? <PremiumActiveBadge />
            : canSellPremium() && <PremiumBanner facilityId={facility.facility_id} navigate={navigate} />}

          {/* Suggested actions — show first, very visible */}
          <SuggestedActions
            actions={canSellPremium()
              ? stats.suggested_actions
              : stats.suggested_actions?.filter((a) => !a.cta?.endsWith('/upgrade'))}
            navigate={navigate}
          />

          {/* KPI Cards Row 1 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={CalendarDays}
              label="Prenotazioni oggi"
              value={kpi.today_count}
              color="text-blue-600"
              bgColor="bg-blue-50 dark:bg-blue-900/20"
            />
            <KpiCard
              icon={Calendar}
              label="Prossime"
              value={kpi.upcoming_count}
              color="text-indigo-600"
              bgColor="bg-indigo-50 dark:bg-indigo-900/20"
            />
            <KpiCard
              icon={TrendingUp}
              label="Questo mese"
              value={kpi.month_bookings}
              subtitle={`€${kpi.month_revenue?.toFixed(0)}`}
              trendPct={kpi.bookings_trend_pct}
              color="text-green-600"
              bgColor="bg-green-50 dark:bg-green-900/20"
            />
            <KpiCard
              icon={BarChart3}
              label="Totale prenotazioni"
              value={kpi.total_bookings}
              color="text-purple-600"
              bgColor="bg-purple-50 dark:bg-purple-900/20"
            />
          </div>

          {/* KPI Cards Row 2 - Financial */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              icon={Euro}
              label="Incasso totale"
              value={`€${kpi.total_revenue?.toFixed(0)}`}
              trendPct={kpi.revenue_trend_pct}
              color="text-emerald-600"
              bgColor="bg-emerald-50 dark:bg-emerald-900/20"
            />
            <KpiCard
              icon={CheckCircle}
              label="Pagati"
              value={`€${kpi.paid_total?.toFixed(0)}`}
              color="text-green-600"
              bgColor="bg-green-50 dark:bg-green-900/20"
            />
            <KpiCard
              icon={AlertCircle}
              label="Da incassare (campo)"
              value={kpi.unpaid_at_venue_count}
              subtitle={`€${kpi.unpaid_at_venue_amount?.toFixed(0)}`}
              color="text-orange-600"
              bgColor="bg-orange-50 dark:bg-orange-900/20"
            />
            <KpiCard
              icon={CreditCard}
              label="Incassati online"
              value={`€${kpi.online_collected?.toFixed(0)}`}
              subtitle={`-${kpi.commission_percent}% comm.`}
              color="text-blue-600"
              bgColor="bg-blue-50 dark:bg-blue-900/20"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Day of Week Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Prenotazioni per giorno</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 h-32">
                  {Object.entries(stats.day_of_week_stats || {}).map(([day, count]) => {
                    const maxCount = Math.max(...Object.values(stats.day_of_week_stats || {}), 1);
                    const height = (count / maxCount) * 100;
                    return (
                      <div key={day} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs font-medium">{count}</span>
                        <div
                          className="w-full bg-primary/80 rounded-t transition-all"
                          style={{ height: `${Math.max(height, 4)}%` }}
                        />
                        <span className="text-xs text-muted-foreground">{DAY_NAMES[day]}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Popular Time Slots */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Orari più richiesti</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(stats.popular_slots || []).slice(0, 6).map(([slot, count], i) => {
                    const maxCount = stats.popular_slots?.[0]?.[1] || 1;
                    const pct = (count / maxCount) * 100;
                    return (
                      <div key={slot} className="flex items-center gap-3">
                        <span className="text-sm font-mono w-12">{slot}</span>
                        <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                          <div
                            className="h-full bg-primary/70 rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Heatmap + Top customers (Strutture E) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <OccupancyHeatmap heatmap={stats.slot_heatmap} />
            <TopCustomersList customers={stats.top_customers} />
          </div>

          {/* Monthly Trend */}
          {stats.monthly_trend?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Andamento mensile</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                  {stats.monthly_trend.map(([month, data]) => (
                    <div key={month} className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground font-medium">
                        {new Date(month + '-01').toLocaleDateString('it-IT', { month: 'short', year: '2-digit' })}
                      </p>
                      <p className="text-lg font-bold mt-1">{data.bookings}</p>
                      <p className="text-xs text-muted-foreground">€{data.revenue?.toFixed(0)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Field Stats */}
          {stats.field_stats?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Statistiche per campo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {stats.field_stats.map((fs, i) => (
                    <div key={i} className="p-4 bg-muted/30 rounded-lg border">
                      <p className="font-medium">{fs.name}</p>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Prenotazioni:</span>
                          <span className="font-bold ml-1">{fs.count}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Incasso:</span>
                          <span className="font-bold ml-1">€{fs.revenue?.toFixed(0)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ TAB: PRENOTAZIONI ═══ */}
        <TabsContent value="bookings" className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {[
              ['Oggi', kpi.today_count, 'text-blue-600'],
              ['Prossime', kpi.upcoming_count, 'text-indigo-600'],
              ['Da incassare', `€${kpi.unpaid_at_venue_amount?.toFixed(0)}`, 'text-orange-600'],
            ].map(([label, value, color]) => (
              <Card key={label}>
                <CardContent className="p-3 text-center">
                  <p className={`text-xl font-bold ${color}`}>{value}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => setShowBookingDialog(true)}
              disabled={!stats.fields?.length}
              data-testid="new-booking-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuova prenotazione
            </Button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-3 gap-2 md:max-w-xl">
            <Select value={bookingFilter} onValueChange={setBookingFilter}>
              <SelectTrigger aria-label="Periodo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">Prossime</SelectItem>
                <SelectItem value="past">Passate</SelectItem>
                <SelectItem value="week">Settimana</SelectItem>
                <SelectItem value="all">Tutte</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fieldFilter} onValueChange={setFieldFilter}>
              <SelectTrigger aria-label="Campo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Ogni campo</SelectItem>
                {(stats.fields || []).map(f => (
                  <SelectItem key={f.field_id} value={f.field_id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger aria-label="Pagamento"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Pagamenti</SelectItem>
                <SelectItem value="paid">Pagati</SelectItem>
                <SelectItem value="unpaid">Da pagare</SelectItem>
                <SelectItem value="at_venue">Al campo</SelectItem>
                <SelectItem value="online">Online</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {bookingFilter === 'week' && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateWeek(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">Settimana del {formatDateDisplay(formatDateStr(calendarWeekStart))}</span>
              <Button variant="outline" size="icon" onClick={() => navigateWeek(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Bookings List */}
          {loadingBookings ? (
            <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : allBookings.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">Nessuna prenotazione trovata</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {allBookings.map((b) => (
                <Card key={b.booking_id} className="overflow-hidden">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium">
                          {formatDateDisplay(b.date)} · {b.start_time}–{b.end_time}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {b.field_name} · {b.match_id ? (
                            <Link to={`/matches/${b.match_id}`} className="text-primary hover:underline">
                              {b.display_label || 'Partita'}
                            </Link>
                          ) : (b.display_label || b.booker?.nickname || b.booker?.name || '—')}
                        </p>
                        {b.notes && <p className="text-xs text-muted-foreground mt-0.5">📝 {b.notes}</p>}
                      </div>
                      {b.price && (
                        <span className="font-bold text-lg shrink-0">€{parseFloat(b.price).toFixed(0)}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <PaymentBadge method={b.payment_method} status={b.payment_status} collected={b.collected_online} />
                      <div className="flex gap-1">
                        {b.payment_status !== 'paid' && b.payment_status !== 'not_required' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => setMarkPaidBooking(b)}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" /> Segna pagato
                          </Button>
                        )}
                        {b.date >= formatDateStr(new Date()) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-7 text-destructive"
                            onClick={() => setCancelBooking(b)}
                          >
                            Annulla
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ═══ TAB: PAGAMENTI ═══ */}
        <TabsContent value="payments" className="space-y-6">
          <CollectionsCard facilityId={id} />

          {/* Bank Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Landmark className="h-5 w-5" />
                Dati Bancari
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {bankDetails?.has_bank_details && (
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Dati bancari configurati</span>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Inserisci i dati bancari per ricevere i pagamenti delle prenotazioni online.
                LeagueHub trattiene una commissione del 10% e ti trasferisce il 90% tramite bonifico.
              </p>
              <div className="grid gap-3 max-w-lg">
                <div className="space-y-1">
                  <Label>IBAN *</Label>
                  <Input
                    placeholder="IT60X0542811101000000123456"
                    value={bankForm.iban}
                    onChange={(e) => setBankForm({ ...bankForm, iban: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Intestatario *</Label>
                  <Input
                    placeholder="Nome e cognome o ragione sociale"
                    value={bankForm.holder_name}
                    onChange={(e) => setBankForm({ ...bankForm, holder_name: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>SWIFT/BIC (opzionale)</Label>
                  <Input
                    placeholder="UNCRITMM"
                    value={bankForm.swift}
                    onChange={(e) => setBankForm({ ...bankForm, swift: e.target.value.toUpperCase() })}
                  />
                </div>
                <Button
                  disabled={savingBank || !bankForm.iban || !bankForm.holder_name}
                  onClick={handleSaveBank}
                  className="w-fit"
                >
                  {savingBank ? 'Salvataggio...' : 'Salva Dati Bancari'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Balance Summary */}
          {balanceData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Riepilogo Incassi Online
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">€{balanceData.total_collected?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Totale incassato online</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">€{balanceData.commission_total?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Commissioni ({balanceData.commission_percent}%)</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-green-600">€{balanceData.facility_earned?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Guadagno netto</p>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">€{balanceData.balance_owed?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Da liquidare</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {balanceData.paid_bookings_count} prenotazioni pagate online
                </p>

                <div className="mt-4">
                  <Button onClick={() => setPayoutDialogOpen(true)} disabled={!balanceData || balanceData.balance_owed <= 0}>
                    Richiedi bonifico
                  </Button>
                </div>

                {/* Recent payouts */}
                {balanceData.recent_payouts?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm mb-2">Ultimi bonifici effettuati</h4>
                    <div className="space-y-1">
                      {balanceData.recent_payouts.map((p, i) => (
                        <div key={i} className="flex justify-between text-sm p-2 bg-muted/50 rounded">
                          <span>{new Date(p.created_at).toLocaleDateString('it-IT')}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">€{p.amount?.toFixed(2)}</span>
                            <Badge variant={p.status === 'completed' ? 'default' : 'secondary'}>
                              {p.status === 'completed' ? 'Completato' : p.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ TAB: CAMPI ═══ */}
        <TabsContent value="fields">
          <FacilityFieldsManager facilityId={id} onChange={fetchStats} />
        </TabsContent>
      </Tabs>

      {/* Mark Paid Confirmation Dialog */}
      <Dialog open={!!markPaidBooking} onOpenChange={(open) => !open && setMarkPaidBooking(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma pagamento</DialogTitle>
          </DialogHeader>
          {markPaidBooking && (
            <div className="space-y-3">
              <p className="text-sm">
                Confermi di aver ricevuto il pagamento per questa prenotazione?
              </p>
              <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                <p><strong>Data:</strong> {formatDateDisplay(markPaidBooking.date)}</p>
                <p><strong>Orario:</strong> {markPaidBooking.start_time} - {markPaidBooking.end_time}</p>
                <p><strong>Campo:</strong> {markPaidBooking.field_name}</p>
                <p><strong>Prenotato da:</strong> {markPaidBooking.display_label || markPaidBooking.booker?.name}</p>
                {markPaidBooking.price && (
                  <p><strong>Importo:</strong> €{parseFloat(markPaidBooking.price).toFixed(2)}</p>
                )}
                <p><strong>Metodo:</strong> {markPaidBooking.payment_method === 'at_venue' ? 'Al campo' : 'Online'}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidBooking(null)}>Annulla</Button>
            <Button onClick={() => handleMarkPaid(markPaidBooking.booking_id)}>
              <CheckCircle className="h-4 w-4 mr-1" /> Conferma pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog */}
      <Dialog open={!!cancelBooking} onOpenChange={(open) => !open && setCancelBooking(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Annulla prenotazione</DialogTitle>
          </DialogHeader>
          {cancelBooking && (
            <p className="text-sm">
              {formatDateDisplay(cancelBooking.date)} · {cancelBooking.start_time}–{cancelBooking.end_time} · {cancelBooking.field_name}
              <br />
              <span className="text-muted-foreground">
                {cancelBooking.display_label || cancelBooking.booker?.name}
              </span>
              {cancelBooking.collected_online > 0 && (
                <span className="block mt-2 text-amber-700 dark:text-amber-300">
                  I €{Number(cancelBooking.collected_online).toFixed(2)} pagati in app dai giocatori vengono rimborsati.
                </span>
              )}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelBooking(null)}>Indietro</Button>
            <Button variant="destructive" onClick={() => handleCancelBooking(cancelBooking.booking_id)}>
              Annulla prenotazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FacilityBookingDialog
        facilityId={id}
        fields={stats.fields || []}
        isManager
        open={showBookingDialog}
        onOpenChange={setShowBookingDialog}
        onBooked={() => { fetchBookings(); fetchStats(); }}
      />

      {/* Payout Request Dialog */}
      <Dialog open={payoutDialogOpen} onOpenChange={(open) => !open && setPayoutDialogOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Richiedi bonifico</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Importo da liquidare (saldo disponibile):</p>
            <div className="flex items-center gap-3">
              <Input value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} />
              <span>€</span>
            </div>
            <p className="text-xs text-muted-foreground">Il bonifico verrà eseguito sul conto registrato (IBAN).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutDialogOpen(false)}>Annulla</Button>
            <Button onClick={async () => {
              try {
                await facilitiesAPI.createPayout(id, { amount: parseFloat(payoutAmount) });
                toast.success('Richiesta bonifico inviata');
                setPayoutDialogOpen(false);
                fetchBankInfo();
                fetchStats();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Errore nella richiesta');
              }
            }}>
              Richiedi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────

/** Match collections paid straight to the facility, through its own Stripe account. */
const CollectionsCard = ({ facilityId }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    connectAPI.status(facilityId).then((res) => setStatus(res.data)).catch(() => setStatus({}));
  }, [facilityId]);

  const link = async () => {
    setBusy(true);
    try {
      const res = await connectAPI.link({
        facility_id: facilityId,
        origin: window.location.origin,
        return_path: `/facilities/${facilityId}/dashboard#payments`,
      });
      await openCheckout(res.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Non riesco ad aprire Stripe');
    } finally {
      setBusy(false);
    }
  };

  if (status && !status.enabled) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Raccolte delle partite
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Quando una partita da voi apre la raccolta del campo, i giocatori pagano la quota con carta
          e i soldi arrivano direttamente sul vostro conto, con bonifico automatico di Stripe.
        </p>
        {!status ? (
          <Skeleton className="h-9 w-40" />
        ) : status.ready ? (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Attivo: ricevete le raccolte</span>
          </div>
        ) : (
          <Button onClick={link} disabled={busy} data-testid="connect-stripe-btn">
            {status.connected ? 'Completa la verifica su Stripe' : 'Collega Stripe'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const KpiCard = ({ icon: Icon, label, value, subtitle, color, bgColor, trendPct }) => {
  const trendVisible = trendPct !== undefined && trendPct !== null;
  const isPositive = trendVisible && trendPct > 0;
  const isNegative = trendVisible && trendPct < 0;
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${bgColor}`}>
            <Icon className={`h-5 w-5 ${color}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-1.5">
              <p className="text-2xl font-bold leading-none">{value}</p>
              {trendVisible && (
                <span className={`text-xs font-semibold ${isPositive ? 'text-emerald-600' : isNegative ? 'text-rose-600' : 'text-muted-foreground'}`}>
                  {isPositive ? '↑' : isNegative ? '↓' : '→'} {Math.abs(trendPct).toFixed(0)}%
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// Strutture E components: SuggestedActions, OccupancyHeatmap, TopCustomers,
// PremiumBanner.
// =============================================================================

const SEVERITY_STYLES = {
  warning: 'border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900',
  info: 'border-blue-300 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900',
};

const SuggestedActions = ({ actions, navigate }) => {
  if (!actions?.length) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          💡 Suggerimenti smart
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {actions.map((action, i) => (
            <div
              key={i}
              className={`p-3 rounded-lg border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${SEVERITY_STYLES[action.severity] || SEVERITY_STYLES.info}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{action.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{action.detail}</div>
              </div>
              {action.cta && (
                <Button size="sm" onClick={() => navigate(action.cta)} className="bg-gradient-to-br from-blue-500 to-indigo-600">
                  Vai →
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const OccupancyHeatmap = ({ heatmap }) => {
  // Build a Mon..Sun × hours matrix. Hour range: collect any hour seen in data,
  // fall back to 16-23 typical evening sport hours.
  const allHours = new Set();
  Object.values(heatmap || {}).forEach((dayObj) =>
    Object.keys(dayObj || {}).forEach((h) => allHours.add(Number(h))));
  const defaultHours = [16, 17, 18, 19, 20, 21, 22, 23];
  defaultHours.forEach((h) => allHours.add(h));
  const hours = Array.from(allHours).sort((a, b) => a - b);

  const max = Math.max(
    1,
    ...Object.values(heatmap || {}).flatMap((d) => Object.values(d || {})),
  );

  const cellColor = (count) => {
    if (!count) return 'bg-muted';
    const ratio = count / max;
    if (ratio > 0.75) return 'bg-emerald-700';
    if (ratio > 0.5) return 'bg-emerald-500';
    if (ratio > 0.25) return 'bg-emerald-300';
    return 'bg-emerald-100';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Heatmap occupancy</CardTitle>
        <p className="text-xs text-muted-foreground">Più scuro = più richiesto. Sfrutta i bianchi con promo.</p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-1 text-xs" style={{ gridTemplateColumns: `auto repeat(7, minmax(0, 1fr))` }}>
          <div></div>
          {DAY_NAMES.map((d) => (
            <div key={d} className="text-center text-muted-foreground">{d}</div>
          ))}
          {hours.map((h) => (
            <React.Fragment key={h}>
              <div className="text-right pr-2 text-muted-foreground">{String(h).padStart(2, '0')}h</div>
              {[0, 1, 2, 3, 4, 5, 6].map((wd) => {
                const count = (heatmap?.[wd] || {})[h] || 0;
                return (
                  <div
                    key={wd}
                    title={`${DAY_NAMES[wd]} ${h}h: ${count} prenot.`}
                    className={`aspect-square rounded ${cellColor(count)} flex items-center justify-center text-[10px] font-medium ${count > max / 2 ? 'text-white' : 'text-foreground/70'}`}
                  >
                    {count > 0 ? count : ''}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const TopCustomersList = ({ customers }) => {
  if (!customers?.length) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Top clienti (90gg)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {customers.map((c, i) => (
            <div key={c.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/40">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xs font-bold flex items-center justify-center shrink-0">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">
                  {c.user?.nickname || c.user?.name || 'Sconosciuto'}
                </div>
                <div className="text-xs text-muted-foreground">{c.bookings} prenotazioni</div>
              </div>
              <div className="text-sm font-semibold text-emerald-600">€{c.revenue?.toFixed(0)}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const PremiumBanner = ({ facilityId, navigate }) => (
  <Card className="border-2 border-purple-200 dark:border-purple-900 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/40 dark:to-indigo-950/40">
    <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-2xl">⭐</div>
        <div>
          <h3 className="font-bold text-lg">Passa a Premium</h3>
          <p className="text-sm text-muted-foreground">
            Top results in marketplace · commissione 7% · analytics avanzate · 30gg gratis
          </p>
        </div>
      </div>
      <Button
        onClick={() => navigate(`/facilities/${facilityId}/upgrade`)}
        className="bg-gradient-to-br from-purple-600 to-indigo-600 hover:opacity-90 font-bold"
      >
        Prova 30gg gratis
      </Button>
    </CardContent>
  </Card>
);

const PremiumActiveBadge = () => (
  <Card className="border-2 border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30">
    <CardContent className="p-3 flex items-center gap-3 text-sm">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-sm">⭐</div>
      <div className="flex-1">
        <div className="font-semibold">Account Premium attivo</div>
        <div className="text-xs text-muted-foreground">Top results, badge verificato, commissione ridotta al 7%</div>
      </div>
    </CardContent>
  </Card>
);

const PaymentBadge = ({ method, status, collected }) => {
  if (status === 'partial') {
    return (
      <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-900 text-xs">
        <CreditCard className="h-3 w-3 mr-1" />
        €{Number(collected || 0).toFixed(0)} raccolti in app
      </Badge>
    );
  }
  if (status === 'paid') {
    return (
      <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs">
        <CheckCircle className="h-3 w-3 mr-1" />
        Pagato
      </Badge>
    );
  }
  if (status === 'pending') {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs">
        <Clock className="h-3 w-3 mr-1" />
        In attesa
      </Badge>
    );
  }
  if (method === 'at_venue') {
    return (
      <Badge variant="outline" className="text-xs">
        <Euro className="h-3 w-3 mr-1" />
        Al campo
      </Badge>
    );
  }
  if (status === 'not_required') {
    return (
      <Badge variant="secondary" className="text-xs">—</Badge>
    );
  }
  return <Badge variant="secondary" className="text-xs">{status || '—'}</Badge>;
};

export default FacilityDashboardPage;
